import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Mail, Phone, Facebook, Instagram, MessageCircle, Globe, Menu, X, LogOut, LogIn, LayoutDashboard, ChevronDown, Briefcase, Lightbulb, Stethoscope, Building2, Church, Megaphone, Plus, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { useMultiProfile } from "@/auth/MultiProfileContext";
import { useRegisterModal } from "@/components/RegisterModal";

const KIND_ICON = {
  business: Briefcase,
  venture: Lightbulb,
  professional: Stethoscope,
  ngo: Building2,
  church: Church,
  community_leader: Megaphone,
};
const KIND_COLOR = {
  business: "bg-orange",
  venture: "bg-amber-500",
  professional: "bg-teal",
  ngo: "bg-teal-deep",
  church: "bg-violet-600",
  community_leader: "bg-rose-500",
};

const LOGO_ES = "https://customer-assets.emergentagent.com/job_creator-connect-302/artifacts/axnqaied_LOGO-WEB.png";
const LOGO_EN = "https://customer-assets.emergentagent.com/job_creator-connect-302/artifacts/bo1j0zr3_LOGO-RAS.png";

export const Logo = ({ className = "h-14", invert = false }) => {
  const { lang } = useI18n();
  const src = lang === "en" ? LOGO_EN : LOGO_ES;
  return (
    <Link to="/" className="inline-flex items-center" data-testid="brand-logo-link">
      <img
        src={src}
        alt="Red de Amor y Solidaridad"
        className={`${className} w-auto ${invert ? "" : "mix-blend-multiply"}`}
      />
    </Link>
  );
};

const UserMenu = ({ profiles, active, setActiveId, user, onLogout, t }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Decide what to render in the trigger pill
  const hasProfiles = profiles.length > 0;
  const TriggerIcon = active ? (KIND_ICON[active.kind] || Briefcase) : LayoutDashboard;
  const triggerLabel = active
    ? active.name
    : user?.role === "admin"
      ? t.nav.admin
      : t.nav.dashboard;
  const triggerColor = active ? (KIND_COLOR[active.kind] || "bg-orange") : "bg-teal-deep";

  // Where the dashboard / panel link points
  const panelHref = user?.role === "admin" ? "/admin" : user?.role === "moderator" ? "/admin" : user?.role === "entity" ? "/entity" : "/my-profiles";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-cream border border-gray-200 text-sm text-teal-deep hover:border-orange transition-colors max-w-[260px]"
        data-testid="user-menu-toggle"
      >
        <span className={`w-7 h-7 rounded-full ${triggerColor} text-white flex items-center justify-center shrink-0`}>
          <TriggerIcon size={12} />
        </span>
        <span className="truncate font-semibold">{triggerLabel}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50" data-testid="user-menu">
          {/* User header */}
          <div className="px-4 py-3 bg-cream border-b border-gray-100">
            <p className="text-xs text-teal-soft">{t.userMenu.signedInAs}</p>
            <p className="text-sm font-semibold text-teal-deep truncate">{user?.full_name || user?.email}</p>
            <p className="text-[10px] uppercase tracking-widest font-bold text-orange mt-1">
              {hasProfiles ? t.profileSwitcher.eyebrow : t.userMenu.noProfilesYet}
            </p>
          </div>

          {/* Profiles list (only if user has any) */}
          {hasProfiles && (
            <ul className="max-h-60 overflow-y-auto py-1 border-b border-gray-100">
              {profiles.map((p) => {
                const I = KIND_ICON[p.kind] || Briefcase;
                const isActive = active && active.id === p.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => { setActiveId(p.id); setOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-cream transition-colors ${isActive ? "bg-orange/5" : ""}`}
                      data-testid={`user-menu-profile-${p.id}`}
                    >
                      <span className={`w-7 h-7 rounded-full ${KIND_COLOR[p.kind] || "bg-orange"} text-white flex items-center justify-center shrink-0`}>
                        <I size={13} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-teal-deep truncate">{p.name}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-teal-soft">{p.kind.replace("_", " ")}</span>
                      </span>
                      {isActive && <Check size={14} className="text-orange shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Actions */}
          <div className="p-2">
            <Link
              to={panelHref}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-teal-deep hover:bg-cream rounded-lg"
              data-testid="user-menu-panel"
            >
              <LayoutDashboard size={14} /> {user?.role === "admin" ? t.nav.admin : t.userMenu.myPanel}
            </Link>
            {user?.role !== "admin" && user?.role !== "entity" && (
              <Link
                to="/profiles/new"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-orange hover:bg-orange/5 rounded-lg"
                data-testid="user-menu-add"
              >
                <Plus size={14} /> {t.profileSwitcher.addProfile}
              </Link>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg"
              data-testid="user-menu-logout"
            >
              <LogOut size={14} /> {t.nav.logout}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileSwitcher = () => null;


const TopBar = () => {
  const { t, lang, toggle } = useI18n();
  const { user } = useAuth();
  return (
    <div className="bg-teal text-white text-sm">
      <div className="container-tight flex flex-wrap items-center justify-between gap-2 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="hidden sm:inline">❤</span>
          <span className="truncate">{t.topbar.tagline}</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="mailto:info@lovesolidarity.com" className="hidden md:inline-flex items-center gap-2 hover:text-orange transition-colors">
            <Mail size={14} /> info@lovesolidarity.com
          </a>
          <a href="tel:+14323382869" className="hidden md:inline-flex items-center gap-2 hover:text-orange transition-colors">
            <Phone size={14} /> +1 (432) 338-2869
          </a>
          {!user && (
            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider hover:text-orange transition-colors border-l border-white/20 pl-4" data-testid="topbar-login">
              <LogIn size={12} /> {t.nav.login}
            </Link>
          )}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="inline-flex items-center gap-1 border border-white/30 hover:border-orange hover:text-orange rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <Globe size={12} /> {lang === "es" ? "EN" : "ES"}
          </button>
          <div className="flex items-center gap-2">
            <a href="https://www.facebook.com/redrasapp" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-orange"><Facebook size={14} /></a>
            <a href="https://www.instagram.com/redrasapp/" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-orange"><Instagram size={14} /></a>
            <a href="https://wa.me/14323382869" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="hover:text-orange"><MessageCircle size={14} /></a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { items: profiles, active, setActiveId } = useMultiProfile();
  const { open: openRegister } = useRegisterModal();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const linkClass = ({ isActive }) =>
    `text-sm font-semibold uppercase tracking-wider transition-colors ${isActive ? "text-orange" : "text-teal hover:text-orange"}`;

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100">
      <TopBar />
      <div className="container-tight flex items-center justify-between py-3">
        <Logo className="h-20" />
        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/directory" className={linkClass} data-testid="nav-directory">{t.nav.directory}</NavLink>
          <NavLink to="/red-apoyo" className={linkClass} data-testid="nav-redapoyo">{t.redApoyo.navLabel}</NavLink>
          <NavLink to="/eventos" className={linkClass} data-testid="nav-events">{t.nav.events}</NavLink>
          <NavLink to="/conocenos" className={linkClass} data-testid="nav-about">{t.nav.about}</NavLink>
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <UserMenu
              profiles={profiles}
              active={active}
              setActiveId={setActiveId}
              user={user}
              onLogout={handleLogout}
              t={t}
            />
          ) : (
            <button type="button" onClick={openRegister} className="btn-orange" data-testid="nav-register">{t.nav.register}</button>
          )}
        </div>
        <button
          className="lg:hidden text-teal"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="container-tight py-4 flex flex-col gap-3">
            <NavLink to="/directory" className={linkClass} onClick={() => setOpen(false)}>{t.nav.directory}</NavLink>
            <NavLink to="/red-apoyo" className={linkClass} onClick={() => setOpen(false)}>{t.redApoyo.navLabel}</NavLink>
            <NavLink to="/eventos" className={linkClass} onClick={() => setOpen(false)}>{t.nav.events}</NavLink>
            <NavLink to="/conocenos" className={linkClass} onClick={() => setOpen(false)}>{t.nav.about}</NavLink>
            {user ? (
              <>
                <Link to={user.role === "entity" ? "/entity" : user.role === "admin" ? "/admin" : "/my-profiles"} className="btn-teal-outline w-fit" onClick={() => setOpen(false)}>{t.nav.dashboard}</Link>
                <button onClick={handleLogout} className="text-teal text-left text-sm font-semibold">{t.nav.logout}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-teal text-sm font-semibold uppercase tracking-wider" onClick={() => setOpen(false)}>{t.nav.login}</Link>
                <button type="button" onClick={() => { setOpen(false); openRegister(); }} className="btn-orange w-fit text-left">{t.nav.register}</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  return (
    <footer className="bg-teal-deep text-white/90 mt-24">
      <div className="container-tight grid grid-cols-1 md:grid-cols-4 gap-10 py-16">
        <div>
          <Logo className="h-16" invert />
          <p className="mt-5 text-white/70 italic font-display text-lg leading-snug">
            {t.footer.tag}
          </p>
          <div className="flex items-center gap-3 mt-5 text-white/70">
            <a href="https://www.facebook.com/redrasapp" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-orange"><Facebook size={18} /></a>
            <a href="https://www.instagram.com/redrasapp/" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-orange"><Instagram size={18} /></a>
            <a href="https://wa.me/14323382869" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="hover:text-orange"><MessageCircle size={18} /></a>
          </div>
        </div>
        <div>
          <h4 className="eyebrow text-orange mb-4">{t.footer.links}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-orange">{t.nav.home}</Link></li>
            <li><Link to="/directory" className="hover:text-orange">{t.nav.directory}</Link></li>
            <li><Link to="/eventos" className="hover:text-orange">{t.nav.events}</Link></li>
            <li><Link to="/conocenos" className="hover:text-orange">{t.nav.about}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow text-orange mb-4">{t.footer.services}</h4>
          <ul className="space-y-2 text-sm">
            {t.services.items.map((s, i) => (<li key={i}>{s.t}</li>))}
          </ul>
        </div>
        <div>
          <h4 className="eyebrow text-orange mb-4">{t.footer.contactNow}</h4>
          <ul className="space-y-2 text-sm">
            <li>1104 S Crane Ave, Odessa, TX</li>
            <li>+1 (432) 338-2869</li>
            <li>info@lovesolidarity.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">
        © 2026 RED Love and Solidarity · {t.footer.rights}
      </div>
    </footer>
  );
};

export const PageShell = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
